import {
	collection,
	addDoc,
	serverTimestamp,
	query,
	where,
	orderBy,
	getDocs,
	updateDoc,
	doc,
	deleteDoc,
} from "firebase/firestore";
import { db } from "../firebase";
import type { SystemNotification, NewSystemNotification } from "../types";

export const notificationService = {
	// Create a new notification
	async createNotification(
		notification: NewSystemNotification
	): Promise<string> {
		try {
			const docRef = await addDoc(collection(db, "notifications"), {
				...notification,
				createdAt: serverTimestamp(),
			});
			return docRef.id;
		} catch (error) {
			console.error("Error creating notification:", error);
			throw error;
		}
	},

	// Get notifications for a user
	async getUserNotifications(userId: string): Promise<SystemNotification[]> {
		try {
			// Avoid composite index requirement: filter by userId, sort client-side
			const q = query(
				collection(db, "notifications"),
				where("userId", "==", userId)
			);

			const querySnapshot = await getDocs(q);
			const items = querySnapshot.docs.map((d) => ({
				id: d.id,
				...d.data(),
				createdAt: (d.data() as any).createdAt?.toDate?.() || new Date(),
			})) as SystemNotification[];
			return items.sort(
				(a, b) => b.createdAt.getTime() - a.createdAt.getTime()
			);
		} catch (error) {
			console.error("Error fetching notifications:", error);
			throw error;
		}
	},

	// Mark notification as read
	async markAsRead(notificationId: string): Promise<void> {
		try {
			const notificationRef = doc(db, "notifications", notificationId);
			await updateDoc(notificationRef, {
				isRead: true,
			});
		} catch (error) {
			console.error("Error marking notification as read:", error);
			throw error;
		}
	},

	// Delete notification (dismiss)
	async deleteNotification(notificationId: string): Promise<void> {
		try {
			await deleteDoc(doc(db, "notifications", notificationId));
		} catch (e) {
			console.error("Error deleting notification:", e);
			throw e;
		}
	},

	// Create comment deletion notification
	async createCommentDeletionNotification(
		userId: string,
		originalComment: string,
		reason: string,
		guideId?: string,
		postId?: string
	): Promise<string> {
		const related: Record<string, any> = {
			originalComment,
			reason,
		};
		if (guideId) related.guideId = guideId;
		if (postId) related.postId = postId;

		const notification: NewSystemNotification = {
			userId,
			type: "comment_deletion",
			title: "תגובה נמחקה על ידי מנהל",
			message: `תגובתך נמחקה על ידי מנהל האתר. סיבה: ${reason}`,
			isRead: false,
			relatedData: related,
		};

		return this.createNotification(notification);
	},

	// Create a user reply/appeal to admin team
	async createUserReplyToAdmin(
		userId: string,
		originalNotificationId: string,
		message: string
	): Promise<string> {
		const docRef = await addDoc(collection(db, "notificationAppeals"), {
			userId,
			notificationId: originalNotificationId,
			message,
			status: "open",
			createdAt: serverTimestamp(),
		});
		return docRef.id;
	},

	// Get unread notifications count
	async getUnreadCount(userId: string): Promise<number> {
		try {
			const q = query(
				collection(db, "notifications"),
				where("userId", "==", userId),
				where("isRead", "==", false)
			);

			const querySnapshot = await getDocs(q);
			return querySnapshot.size;
		} catch (error) {
			console.error("Error getting unread count:", error);
			return 0;
		}
	},

	// Create like notification
	async createLikeNotification(
		targetUserId: string,
		likerUserId: string,
		likerUserName: string,
		guideId?: string,
		postId?: string
	): Promise<string> {
		const related: Record<string, any> = {
			likeUserId: likerUserId,
			likeUserName: likerUserName,
		};
		if (guideId) related.guideId = guideId;
		if (postId) related.postId = postId;

		const notification: NewSystemNotification = {
			userId: targetUserId,
			type: "like",
			title: "קיבלת לייק! ❤️",
			message: `${likerUserName} אהב את התוכן שלך`,
			isRead: false,
			relatedData: related,
		};

		return this.createNotification(notification);
	},

	// Create comment notification
	async createCommentNotification(
		targetUserId: string,
		commenterUserId: string,
		commenterUserName: string,
		commentContent: string,
		guideId?: string,
		postId?: string,
		parentCommentId?: string,
		parentCommentContent?: string
	): Promise<string> {
		const related: Record<string, any> = {
			commentUserId: commenterUserId,
			commentUserName: commenterUserName,
			commentContent: commentContent,
		};
		if (guideId) related.guideId = guideId;
		if (postId) related.postId = postId;
		if (parentCommentId) related.parentCommentId = parentCommentId;
		if (parentCommentContent)
			related.parentCommentContent = parentCommentContent;

		const notification: NewSystemNotification = {
			userId: targetUserId,
			type: "comment",
			title: "קיבלת תגובה 💬",
			message: `${commenterUserName} הגיב על התוכן שלך`,
			isRead: false,
			relatedData: related,
		};

		return this.createNotification(notification);
	},

	// Create view milestone notification
	async createViewMilestoneNotification(
		targetUserId: string,
		guideId: string,
		viewsCount: number
	): Promise<string> {
		const notification: NewSystemNotification = {
			userId: targetUserId,
			type: "view_milestone",
			title: "יעד צפיות! 👁️",
			message: `המדריך שלך הגיע ל-${viewsCount} צפיות`,
			isRead: false,
			relatedData: {
				guideId: guideId,
				viewsCount: viewsCount,
			},
		};

		return this.createNotification(notification);
	},

	// Create forum post notification
	async createForumPostNotification(
		targetUserId: string,
		postId: string,
		postTitle: string,
		authorId: string,
		authorName: string
	): Promise<string> {
		const notification: NewSystemNotification = {
			userId: targetUserId,
			type: "forum_post",
			title: "פוסט חדש בפורום 💬",
			message: `${authorName} פרסם פוסט חדש: "${postTitle}"`,
			isRead: false,
			relatedData: {
				postId: postId,
				forumPostTitle: postTitle,
			},
		};

		return this.createNotification(notification);
	},

	// Create forum reply notification
	async createForumReplyNotification(
		targetUserId: string,
		postId: string,
		postTitle: string,
		replyId: string,
		replyContent: string,
		replierId: string,
		replierName: string
	): Promise<string> {
		const notification: NewSystemNotification = {
			userId: targetUserId,
			type: "forum_reply",
			title: "תגובה חדשה בפורום 💬",
			message: `${replierName} השיב על הפוסט שלך: "${postTitle}"`,
			isRead: false,
			relatedData: {
				postId: postId,
				forumPostTitle: postTitle,
				forumReplyId: replyId,
				forumReplyContent: replyContent,
			},
		};

		return this.createNotification(notification);
	},

	// Create contact form notification for admins
	async createContactFormNotification(
		adminUserId: string,
		contactName: string,
		subject: string,
		category: string,
		contactFormId: string
	): Promise<string> {
		const notification: NewSystemNotification = {
			userId: adminUserId,
			type: "contact_form",
			title: "פנייה חדשה דרך צור קשר 📬",
			message: `${contactName} שלח פנייה בנושא: "${subject}" (קטגוריה: ${category})`,
			isRead: false,
			relatedData: {
				contactFormId: contactFormId,
				contactName: contactName,
				subject: subject,
				category: category,
			},
		};

		return this.createNotification(notification);
	},
};
